import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import NewsletterForm from "@/components/NewsletterForm";

export default function Home() {
  const { t, lang } = useI18n();
  const location = useLocation();

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      // Wait one tick for sections to render
      requestAnimationFrame(() => {
        const el = document.getElementById(target);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.state]);

  return (
    <main data-testid="home-page">
      {/* Hero — full-bleed skyline background with contained content */}
      <section
        className="relative bg-[#0D1B2A] text-white overflow-hidden"
        data-testid="hero-section"
      >
        <img
          src="/images/hero-skyline.jpg"
          alt={t("hero.imageAlt")}
          data-testid="hero-image"
          className="absolute inset-0 h-full w-full object-cover object-center"
          loading="eager"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(13,27,42,0.92) 0%, rgba(13,27,42,0.82) 45%, rgba(13,27,42,0.35) 100%)",
          }}
        />

        <div className="relative mx-auto max-w-7xl px-5 md:px-8 py-20 md:py-28 lg:py-32">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-6">
              {t("hero.eyebrow")}
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed max-w-xl">
              {t("hero.subtitle")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={`/${lang}#newsletter`}
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center h-12 px-7 bg-[#D4AF37] text-[#0D1B2A] text-xs font-semibold uppercase tracking-widest hover:bg-[#c19d2b] no-underline"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("newsletter")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {t("hero.primaryCta")}
              </a>
              <a
                href={`/${lang}#how-it-works`}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center h-12 px-7 border border-white/30 text-white text-xs font-semibold uppercase tracking-widest hover:bg-white/10 no-underline"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("how-it-works")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {t("hero.secondaryCta")}
              </a>
            </div>
          </div>

          <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-4xl">
            <div className="border-l-2 border-[#D4AF37] pl-4">
              <p className="text-[11px] uppercase tracking-widest text-white/50">01</p>
              <p className="mt-1 text-sm text-white/90">{t("trust.regulator")}</p>
            </div>
            <div className="border-l-2 border-[#D4AF37] pl-4">
              <p className="text-[11px] uppercase tracking-widest text-white/50">02</p>
              <p className="mt-1 text-sm text-white/90">{t("trust.since")}</p>
            </div>
            <div className="border-l-2 border-[#D4AF37] pl-4">
              <p className="text-[11px] uppercase tracking-widest text-white/50">03</p>
              <p className="mt-1 text-sm text-white/90">{t("trust.investors")}</p>
            </div>
            <div className="border-l-2 border-[#D4AF37] pl-4">
              <p className="text-[11px] uppercase tracking-widest text-white/50">04</p>
              <p className="mt-1 text-sm text-white/90">{t("trust.funded")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About / What is crowdlending */}
      <section id="about" data-testid="about-section" className="bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              02 — {t("about.title")}
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-2xl"
              style={{ fontFamily: "'Playfair Display', serif", color: "#0D1B2A" }}
            >
              {t("about.title")}
            </h2>
            <div className="mt-8 space-y-6 text-[#1B263B]/85 text-base leading-relaxed max-w-2xl">
              <p>{t("about.paragraph1")}</p>
              <p>{t("about.paragraph2")}</p>
              <p>{t("about.paragraph3")}</p>
              <p>{t("about.paragraph4")}</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src="/images/modern-exterior.jpg"
              alt={t("about.imageAlt")}
              data-testid="about-image"
              className="w-full h-[420px] md:h-[560px] object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" data-testid="how-section" className="bg-white">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <img
              src="/images/how-it-works.jpg"
              alt={t("howItWorks.imageAlt")}
              data-testid="how-image"
              className="w-full h-[420px] md:h-[560px] object-cover"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              03 — {t("howItWorks.title")}
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-2xl"
              style={{ fontFamily: "'Playfair Display', serif", color: "#0D1B2A" }}
            >
              {t("howItWorks.title")}
            </h2>
            <div className="mt-8 space-y-6 text-[#1B263B]/85 text-base leading-relaxed max-w-2xl">
              <p>{t("howItWorks.paragraph1")}</p>
              <p>{t("howItWorks.paragraph2")}</p>
              <p>{t("howItWorks.paragraph3")}</p>
              <p>{t("howItWorks.paragraph4")}</p>
              <p>{t("howItWorks.paragraph5")}</p>
              <p>{t("howItWorks.paragraph6")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps grid */}
      <section id="steps" data-testid="steps-section" className="bg-[#0D1B2A] text-white">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">04</p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("steps.title")}
            </h2>
            <p className="mt-4 text-white/70 text-base md:text-lg leading-relaxed">
              {t("steps.subtitle")}
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {["one", "two", "three", "four"].map((key) => (
              <div
                key={key}
                data-testid={`step-card-${key}`}
                className="bg-white/[0.04] border border-white/10 p-7"
              >
                <p className="text-[#D4AF37] text-sm font-semibold tracking-widest">
                  {t(`steps.${key}.number`)}
                </p>
                <h3
                  className="mt-4 text-xl md:text-2xl leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {t(`steps.${key}.title`)}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {t(`steps.${key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section
        id="newsletter"
        data-testid="newsletter-section"
        className="bg-[#E0E1DD]"
      >
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              05 — {t("newsletter.eyebrow")}
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-xl"
              style={{ fontFamily: "'Playfair Display', serif", color: "#0D1B2A" }}
            >
              {t("newsletter.title")}
            </h2>
            <p className="mt-6 text-[#1B263B]/80 text-base md:text-lg leading-relaxed max-w-md">
              {t("newsletter.description")}
            </p>

            <div className="mt-10 pt-8 border-t border-[#0D1B2A]/15 max-w-md">
              <p className="text-xs uppercase tracking-widest text-[#0D1B2A]/60">
                {t("footer.legalDetails.companyName")}
              </p>
              <p className="mt-2 text-sm text-[#0D1B2A]/80">
                {t("footer.legalDetails.contact")}
              </p>
              <p className="mt-1 text-sm text-[#0D1B2A]/80">
                {t("footer.legalDetails.address")}
              </p>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="bg-white p-8 md:p-10 border border-[#0D1B2A]/10 shadow-sm">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
